import { Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useAWSWebsocket } from '../../../Hooks/useAWSWebsocket';
import { useToast } from '../../../Hooks/useToast';
import { useUser } from '../../../Hooks/useUser';
import { useCreateApplicationMutation } from '../../../Redux/api/applicationApi';
import { jobApi } from '../../../Redux/api/jobApi';
import { useCounterOfferMutation, useCreateOfferMutation } from '../../../Redux/api/offerApi';
import { talentApi } from '../../../Redux/api/talentApi';
import { useAppDispatch } from '../../../Redux/store';
import { KaziAvatar } from '../../../Shared Components/Kazi/KaziAvatar';
import { IApplicationAnswer } from '../../../Types/Application';
import { Job } from '../../../Types/Job';
import { NotificationType } from '../../../Types/Notification';
import { getPayRate } from '../../../Utils/String';
import { fileURLToPath } from 'url';
import { clientApi } from '../../../Redux/api/clientApi';
import { KaziButton } from '../../../Shared Components/Kazi/KaziButton';

interface JobApplyModalForm {
    paymentAccepted: boolean | undefined;
    counterAmount: number | undefined;
    answers: string[]
}

interface JobApplyModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    posting: Job | undefined;
    clientInfo: {
        locator: string;
        first_name: string;
        last_name: string;
        profile_picture?: string;
        company: string;
    }
    
}

export const JobApplyModal = ( { open, setOpen, posting, clientInfo }: JobApplyModalProps ) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { showToast } = useToast();
    const { user, getUsername } = useUser();
    const userId = user?.locator;
    const { sendNotification } = useAWSWebsocket();
    const [createApplication, {data: applicationResponse, isLoading: applicationResponseLoading, isSuccess: applicationSuccesful}] = useCreateApplicationMutation();    
    const [createOffer, {data: createResponse, isLoading: createResponseLoading}] = useCreateOfferMutation();
    const [counterOffer, {data: counterResponse, isLoading: counterResponseLoading}] = useCounterOfferMutation();
    const {
        control,
        formState: {isDirty, dirtyFields, errors, isValid},
        getValues,
        setValue,
        watch,
        handleSubmit
    } = useForm<JobApplyModalForm>({
        defaultValues: {
            paymentAccepted: undefined,
            counterAmount: undefined,
            answers: posting?.questions?.map(() => ' ') ?? []
        }
    })

    const { fields } = useFieldArray({
        // @ts-ignore
        name: 'answers',
        control
    })
    
    // /* ******************** Variables ******************** */
    // const [paymentAccepted, setPaymentAccepted] = useState<boolean | undefined>(undefined);
    // const [counterAmount, setCounterAmount] = useState<number | undefined>(undefined);
    /* ******************** Functions ******************** */
    const handleClose = () => {
        // reset();
        setOpen(false);
    }
    
    const handleApply = (data: JobApplyModalForm) =>{
        createApplication({
            talent_locator: userId!,
            job_locator: posting?.locator!,
            client_locator: clientInfo.locator!,
            status: 'submitted',
            application_date: moment().utc().format(),
            application_form: getValues('answers').map((answer, idx) => ({question_id: idx, answer}))
        })
        .unwrap()
        .then(res => {
            // createOffer({
            //     application_locator: posting?.locator!,
            //     price: posting?.pay_amount!,
            //     from: clientInfo.locator!,
            //     to: userId!
            // })
            // .unwrap()
            // .then( res => {
            //     if (!data.paymentAccepted && data.counterAmount) {
            //         counterOffer({
            //             locator: res.locator,
            //             body: {
            //                 price: String(data.counterAmount),
            //                 user_locator: userId!
            //             }
            //         })
            //         .unwrap()
            //         .catch(err => {
            //             console.log(err);
            //             showToast({
            //                 text: "Failed to send counter offer",
            //                 variant: 'error'
            //             })
            //         })
            //     }
            // })
            // .catch(err => {
            //     console.log(err);
            //     showToast({
            //         text: "Something went wrong",
            //         variant: 'error'
            //     })
            // })
            dispatch(talentApi.util.invalidateTags(['TalentData']));
            dispatch(jobApi.util.invalidateTags(['Job']));
            dispatch(clientApi.util.invalidateTags(['ClientData']));
            handleClose()
        })
        .catch(err => {
            console.log(err);
            showToast({
                text: "Something went wrong",
                variant: 'error'
            })
        })
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    if (!userId) return;
    return (
        <Modal
            open={open}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Card
                sx={{
                    width: "600px",
                    borderRadius: "12px",
                    maxHeight: "95%",
                    overflowY: 'auto'
                }}
            >
                <CardHeader 
                    sx={{
                        paddingBottom: 1
                    }}                
                    action={
                        <IconButton onClick={handleClose}>
                            <Close color='primary'/>
                        </IconButton>
                    }
                />
                <CardContent
                    sx={{
                        padding: '0 24px',
                    }}
                >
                    <Grid container display={"flex"} flexDirection={"column"} gap={'20px'} sx={{ overflowY: 'auto'}}>
                        <Grid display={"flex"} flexDirection={"row"} gap={'16px'}>
                            <Grid display={"flex"} flexDirection={'column'} justifyContent={'center'}>
                                <KaziAvatar locator={clientInfo.locator} first_name={clientInfo.first_name} last_name={clientInfo.last_name} width={'32px'} />
                            </Grid>
                            <Grid display={"flex"} flexDirection={"column"} gap={'8px'}>
                                <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>{posting?.title}</Typography> 
                                <Typography variant='body-medium' color={'primary'} >
                                    {`${clientInfo?.company} · ${posting?.type} ($${posting?.pay_amount}${getPayRate(posting?.pay_rate ?? '')}) · ${posting?.work_site}`}
                                </Typography> 
                            </Grid>
                        </Grid>
                        <Grid item xs display={"flex"} flexDirection={"column"}>
                            <Typography variant='body-medium' color={'primary.main'} >
                                To be considered for this job, your profile will be shared with the client, and you will receive a notification regarding the status of your application. 
                                It's essential that you keep your profile updated to accurately showcase your skills and experience
                            </Typography> 
                        </Grid>
                        {
                            (posting?.questions?.length ?? 0) > 0 &&
                            <Grid item xs display={"flex"} flexDirection={"column"} gap={'12px'}>
                                {
                                    fields.map((field, idx) => (
                                        <Grid key={field.id} display={'flex'} flexDirection={'column'} flex={1} gap={1}>                                    
                                            <Typography variant='body-small' color={'black.200'} >{posting?.questions![idx]?.question}</Typography>                                             
                                            <Controller 
                                                // @ts-ignore
                                                name={`answers.${idx}`}
                                                control={control}
                                                rules={{required: true}}
                                                render={({ field }) =>(                                            
                                                    <TextField 
                                                        {...field}
                                                        multiline
                                                        maxRows={5}
                                                        placeholder='Add a short answer'
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    ))
                                }
                            </Grid>
                        }
                        {
                            posting?.type == 'Short-term' &&
                            <Grid item xs display={"flex"} flexDirection={"row"} gap={'12px'}>
                                <Grid display={'flex'} flexDirection={'column'} flex={1} gap={1}>
                                    {/* <Typography variant='body-small' color={'black.200'} >Do you accept the payment amount?</Typography> 
                                    <Controller 
                                        name="paymentAccepted"
                                        control={control}
                                        rules={{required: true}}
                                        render={({ field }) =>(                     
                                            <TextField
                                                {...field}
                                                error={hasErrors(field.name, errors)}
                                                // helperText={hasErrors(field.name, errors)?"Required":""}
                                                placeholder="Company Name"
                                                sx={{
                                                    marginBottom: "16px",
                                                    width: "300px"
                                                }}
                                            />
                                        )}
                                    />
                                    <FormControl>
                                        <InputLabel>{paymentAccepted == undefined ? 'Select answer' : ''}</InputLabel>
                                        <Select
                                            value={paymentAccepted}
                                            onChange={(params) => {
                                                //@ts-ignore
                                                setPaymentAccepted(params.target.value)
                                            }}                                    
                                        >
                                            { 
                                                [{label: 'Yes', value: true as any}, {label: 'No', value: false as any}].map( option => (
                                                    <MenuItem key={option.label} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl> */}
                                </Grid>
                                <Grid display={'flex'} flexDirection={'column'} flex={1} gap={1}>
                                    {/* <Typography variant='body-small' color={'black.200'} >What amount would you like to be paid?</Typography>
                                    <NumericFormat
                                        customInput={TextField}
                                        placeholder='Enter amount'
                                        thousandSeparator=','
                                        prefix='$'
                                        disabled={paymentAccepted == undefined || paymentAccepted}
                                        value={counterAmount}
                                        onChange={(params) => setCounterAmount(Number(removeNonDigits(params.target.value)))}
                                    /> */}
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </CardContent>
                <CardActions
                    sx={{
                        padding: '24px'
                    }}
                >
                    <Box flex={1} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>  
                        <KaziButton 
                            text='Apply'
                            loading={{
                                isLoading: applicationResponseLoading,
                                loadingText: 'Applying...',
                                color: 'secondary'
                            }}
                            variant={'contained'} 
                            disabled={!isValid || !getValues('answers').every( a => a.length > 1)} 
                            onClick={handleSubmit(handleApply)}
                            sx={{
                                "&.Mui-disabled": {
                                    backgroundColor: 'black.50',
                                    border: 'none',
                                    color: 'primary.main'
                                }
                            }}
                        />        
                    </Box>
                </CardActions>
            </Card> 
        </Modal>
    )
}